/*
    This file uses code from @react-three/drei.
    Original Copyright (c) 2020 react-spring
    Source: https://github.com/pmndrs/drei
    License: MIT (see Frontend/src/THIRD-PARTY-LICENSES/drei-MIT.txt)
*/

import { Html, Line } from '@react-three/drei';
import { type ThreeEvent, useThree } from '@react-three/fiber';
import { FC, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { DoubleSide, type Group, MathUtils, Matrix4, Plane, Ray, Vector3 } from 'three';

import { context } from 'app/modules/wallforming/components/scenes/pivot-controls/context';
import { EPSILON } from 'shared/constants';

const clickDir = /* @__PURE__ */ new Vector3();
const intersectionDir = /* @__PURE__ */ new Vector3();

const toDegrees = (radians: number) => (radians * 180) / Math.PI;
const toRadians = (degrees: number) => (degrees * Math.PI) / 180;

const calculateAngle = (
    clickPoint: Vector3,
    intersectionPoint: Vector3,
    origin: Vector3,
    e1: Vector3,
    e2: Vector3
) => {
    clickDir.copy(clickPoint).sub(origin);
    intersectionDir.copy(intersectionPoint).sub(origin);
    const dote1e1 = e1.dot(e1);
    const dote2e2 = e2.dot(e2);
    const uClick = clickDir.dot(e1) / dote1e1;
    const vClick = clickDir.dot(e2) / dote2e2;
    const uIntersection = intersectionDir.dot(e1) / dote1e1;
    const vIntersection = intersectionDir.dot(e2) / dote2e2;
    const angleClick = Math.atan2(vClick, uClick);
    const angleIntersection = Math.atan2(vIntersection, uIntersection);
    return angleIntersection - angleClick;
};

const fmod = (num: number, denom: number) => {
    let k = Math.floor(num / denom);
    k = k < 0 ? k + 1 : k;

    return num - k * denom;
};

const minimizeAngle = (angle: number) => {
    let result = fmod(angle, 2 * Math.PI);

    if (Math.abs(result) < EPSILON) {
        return 0.0;
    }

    if (result < 0.0) {
        result += 2 * Math.PI;
    }

    return result;
};

const rotMatrix = /* @__PURE__ */ new Matrix4();
const posNew = /* @__PURE__ */ new Vector3();
const ray = /* @__PURE__ */ new Ray();
const intersection = /* @__PURE__ */ new Vector3();

export const AxisRotator: FC<{ dir1: Vector3; dir2: Vector3; axis: 0 | 1 | 2 }> = ({
    dir1,
    dir2,
    axis
}) => {
    const {
        rotationLimits,
        annotations,
        annotationsClass,
        depthTest,
        scale,
        lineWidth,
        fixed,
        axisColors,
        hoveredColor,
        selectedColor,
        renderOrder,
        onDragStart,
        onDrag,
        onDragEnd,
        userData,
        selectedControl,
        setSelectedControl
    } = useContext(context);

    const gl = useThree((state) => state.gl);
    const camControls = useThree((state) => state.controls) as unknown as { enabled: boolean } | undefined;
    const camControlsRef = useRef(camControls);
    const divRef = useRef<HTMLDivElement>(null!);
    const objRef = useRef<Group>(null!);
    const angle0 = useRef<number>(0);
    const angle = useRef<number>(0);
    const clickInfo = useRef<{
        clickPoint: Vector3;
        origin: Vector3;
        e1: Vector3;
        e2: Vector3;
        normal: Vector3;
        plane: Plane;
    } | null>(null);

    const [isHovered, setIsHovered] = useState(false);

    const isSelected = selectedControl?.component === 'Rotator' && selectedControl?.axis === axis;
    const color = isHovered
        ? (hoveredColor ?? axisColors[axis])
        : selectedControl
          ? isSelected
              ? (selectedColor ?? axisColors[axis])
              : '#696969'
          : axisColors[axis];

    const opacity = isSelected || isHovered ? 1 : 0.6;

    const onPointerDown = useCallback(
        (e: ThreeEvent<PointerEvent>) => {
            setSelectedControl({ component: 'Rotator', axis });

            if (annotations) {
                divRef.current.innerText = `${toDegrees(angle.current).toFixed(0)}º`;
                divRef.current.style.display = 'block';
            }
            e.stopPropagation();
            const clickPoint = e.point.clone();
            const origin = new Vector3().setFromMatrixPosition(objRef.current.matrixWorld);
            const e1 = new Vector3().setFromMatrixColumn(objRef.current.matrixWorld, 0).normalize();
            const e2 = new Vector3().setFromMatrixColumn(objRef.current.matrixWorld, 1).normalize();
            const normal = new Vector3().setFromMatrixColumn(objRef.current.matrixWorld, 2).normalize();
            const plane = new Plane().setFromNormalAndCoplanarPoint(normal, origin);
            clickInfo.current = { clickPoint, origin, e1, e2, normal, plane };
            onDragStart({ component: 'Rotator', axis, origin, directions: [e1, e2, normal] });
            camControls && camControlsRef.current && (camControlsRef.current.enabled = false);

            // @ts-expect-error - releasePointerCapture & PointerEvent#pointerId is not in the type definition
            e.target.setPointerCapture(e.pointerId);
        },
        [setSelectedControl, axis, annotations, onDragStart, camControls]
    );

    const onPointerMove = useCallback(
        (e: ThreeEvent<PointerEvent>) => {
            e.stopPropagation();
            gl.domElement.style.setProperty('cursor', 'pointer');
            if (!isHovered) {
                setIsHovered(true);
            }
            if (clickInfo.current) {
                const { clickPoint, origin, e1, e2, normal, plane } = clickInfo.current;
                const [min, max] = rotationLimits?.[axis] || [undefined, undefined];

                ray.copy(e.ray);
                ray.intersectPlane(plane, intersection);
                ray.direction.negate();
                ray.intersectPlane(plane, intersection);
                let deltaAngle = calculateAngle(clickPoint, intersection, origin, e1, e2);
                let degrees = toDegrees(deltaAngle);

                if (e.shiftKey) {
                    degrees = Math.round(degrees / 10) * 10;
                    deltaAngle = toRadians(degrees);
                }

                if (min !== undefined && max !== undefined && max - min < 2 * Math.PI) {
                    deltaAngle = minimizeAngle(deltaAngle);
                    deltaAngle = deltaAngle > Math.PI ? deltaAngle - 2 * Math.PI : deltaAngle;
                    deltaAngle = MathUtils.clamp(deltaAngle, min - angle0.current, max - angle0.current);
                    angle.current = angle0.current + deltaAngle;
                } else {
                    angle.current = minimizeAngle(angle0.current + deltaAngle);
                    angle.current = angle.current > Math.PI ? angle.current - 2 * Math.PI : angle.current;
                }

                if (annotations) {
                    degrees = toDegrees(angle.current);
                    divRef.current.innerText = `${degrees.toFixed(0)}º`;
                }
                rotMatrix.makeRotationAxis(normal, deltaAngle);
                posNew.copy(origin).applyMatrix4(rotMatrix).sub(origin).negate();
                rotMatrix.setPosition(posNew);
                onDrag(rotMatrix);
            }
        },
        [gl.domElement.style, isHovered, rotationLimits, axis, annotations, onDrag]
    );

    const onPointerUp = useCallback(
        (e: ThreeEvent<PointerEvent>) => {
            if (annotations) {
                divRef.current.style.display = 'none';
            }
            e.stopPropagation();
            angle0.current = angle.current;
            clickInfo.current = null;
            onDragEnd();
            camControls && camControlsRef.current && (camControlsRef.current.enabled = true);

            // @ts-expect-error - releasePointerCapture & PointerEvent#pointerId is not in the type definition
            e.target.releasePointerCapture(e.pointerId);
        },
        [annotations, camControls, onDragEnd]
    );

    const onPointerOut = useCallback(
        (e: ThreeEvent<PointerEvent>) => {
            e.stopPropagation();
            setIsHovered(false);
            gl.domElement.style.setProperty('cursor', 'auto');
        },
        [gl.domElement.style]
    );

    const matrixL = useMemo(() => {
        const dir1N = dir1.clone().normalize();
        const dir2N = dir2.clone().normalize();
        return new Matrix4().makeBasis(dir1N, dir2N, dir1N.clone().cross(dir2N));
    }, [dir1, dir2]);

    const r = fixed ? 0.65 : scale * 0.65;

    const arc = useMemo(() => {
        const segments = 32;
        const points: Vector3[] = [];
        for (let j = 0; j <= segments; j++) {
            const angle = (j * (Math.PI / 2)) / segments;
            points.push(new Vector3(Math.cos(angle) * r, Math.sin(angle) * r, 0));
        }
        return points;
    }, [r]);

    return (
        <group
            ref={objRef}
            matrix={matrixL}
            matrixAutoUpdate={false}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerOut={onPointerOut}
            onPointerUp={onPointerUp}
        >
            {annotations && (
                <Html position={[r, r, 0]}>
                    <div
                        ref={divRef}
                        className={annotationsClass}
                        style={{
                            display: 'none',
                            background: '#151520',
                            color: 'white',
                            padding: '6px 8px',
                            borderRadius: 7,
                            whiteSpace: 'nowrap'
                        }}
                    />
                </Html>
            )}

            {/* The invisible mesh being raycast */}
            <Line lineWidth={lineWidth * 4} points={arc} userData={userData} visible={false} />

            {/* The visible mesh */}
            <Line
                color={color}
                depthTest={depthTest}
                fog={false}
                lineWidth={lineWidth}
                opacity={opacity}
                points={arc}
                polygonOffsetFactor={-10}
                raycast={() => null}
                renderOrder={renderOrder}
                side={DoubleSide}
                polygonOffset
                transparent
            />
        </group>
    );
};
